import { i18n } from "../../translate/i18n";

import React, { useState, useEffect, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";

import { Typography, Popover } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Mail, ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import ColorModeContext from "../../layout/themeContext";
import ThemeToggle from "../../components/ThemeToggle";

const CheckIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M5 12L10 17L20 7" />
  </svg>
);

const useStyles = makeStyles((theme) => ({
  "@keyframes loginSpin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes loginFadeIn": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  root: {
    display: "flex",
    width: "100vw",
    minHeight: "100vh",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    padding: "20px",
    backgroundColor: theme.mode === "light" ? "#f5f5f5" : "#0a0a0a",
    transition: "background-color 0.3s ease",
  },

  topRightControls: {
    position: "absolute",
    top: 16,
    right: 20,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  langTriggerButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 8,
    borderRadius: 6,
    transition: "all 0.15s ease",
    "&:hover": {
      background: theme.mode === "dark"
        ? "rgba(255,255,255,0.15)"
        : "rgba(0,0,0,0.06)",
    },
  },
  langTriggerFlag: {
    width: 28,
    height: 20,
    objectFit: "cover",
    borderRadius: 3,
    border: theme.mode === "dark"
      ? "1.5px solid rgba(255,255,255,0.3)"
      : "1.5px solid rgba(0,0,0,0.15)",
    display: "block",
  },

  langPopoverPaper: {
    borderRadius: 14,
    minWidth: 230,
    overflow: "hidden",
    boxShadow: "0 10px 36px rgba(0,0,0,0.18)",
    border: "none",
    background: theme.mode === "dark" ? "#121212" : "#ffffff",
  },
  langHeader: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || "#ffffff",
  },
  langHeaderTitle: {
    fontWeight: 600,
    fontSize: 13,
    color: "inherit",
  },
  langOptionsList: {
    padding: 6,
  },
  langOptionItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "9px 12px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      background: theme.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)",
    },
  },
  langOptionItemSelected: {
    background: "rgba(6, 207, 156, 0.10)",
    "&:hover": { background: "rgba(6, 207, 156, 0.15)" },
  },
  langFlagImg: {
    width: 30,
    height: 20,
    objectFit: "cover",
    borderRadius: 3,
    flexShrink: 0,
    border: theme.mode === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid rgba(0,0,0,0.1)",
  },
  langOptionName: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.mode === "dark" ? "#ffffff" : "#1a1a1a",
    flex: 1,
  },
  langCheckWrapper: {
    width: 18,
    height: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    flexShrink: 0,
  },

  cardWrap: {
    position: "relative",
    width: "100%",
    maxWidth: 460,
    animation: "$loginFadeIn 0.6s ease-out both",
  },
  blurLayer: {
    position: "absolute",
    inset: 0,
    borderRadius: 24,
    overflow: "hidden",
    pointerEvents: "none",
  },
  blurOrb1: {
    position: "absolute",
    top: -40, left: -40, right: -40, bottom: -40,
    borderRadius: "9999px",
    background: theme.mode === "light"
      ? "linear-gradient(to right, transparent, rgba(107,114,128,0.18), transparent)"
      : "linear-gradient(to right, transparent, rgba(156,163,175,0.20), transparent)",
    filter: "blur(20px)",
    opacity: theme.mode === "light" ? 0.4 : 0.5,
    animation: "$loginSpin 10s linear infinite",
  },
  blurOrb2: {
    position: "absolute",
    top: -80, left: -80, right: -80, bottom: -80,
    borderRadius: "9999px",
    background: theme.mode === "light"
      ? "linear-gradient(to right, transparent, rgba(75,85,99,0.12), transparent)"
      : "linear-gradient(to right, transparent, rgba(107,114,128,0.15), transparent)",
    filter: "blur(40px)",
    opacity: theme.mode === "light" ? 0.25 : 0.3,
    animation: "$loginSpin 18s linear infinite reverse",
  },
  borderGradient: {
    position: "absolute",
    inset: 0,
    borderRadius: 24,
    padding: 1,
    background: theme.mode === "light"
      ? "linear-gradient(to bottom, rgba(209,213,219,0.9), rgba(156,163,175,0.7), rgba(107,114,128,0.5))"
      : "linear-gradient(to bottom, rgba(209,213,219,0.4), rgba(75,85,99,0.6), rgba(31,41,55,0.5))",
    pointerEvents: "none",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
  },
  card: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 24,
    boxShadow: theme.mode === "light"
      ? "inset 0 0 0 1px rgba(0,0,0,0.05)"
      : "inset 0 0 0 1px rgba(255,255,255,0.15)",
    backgroundColor: theme.mode === "light"
      ? "hsl(220, 13%, 95%)"
      : "#11141b",
    backgroundImage: theme.mode === "light"
      ? `
        radial-gradient(at 88% 40%, hsl(220, 13%, 95%) 0px, transparent 85%),
        radial-gradient(at 49% 30%, hsl(220, 13%, 95%) 0px, transparent 85%),
        radial-gradient(at 14% 26%, hsl(220, 13%, 95%) 0px, transparent 85%),
        radial-gradient(at 0% 64%, hsl(220, 13%, 82%) 0px, transparent 85%),
        radial-gradient(at 41% 94%, hsl(215, 14%, 86%) 0px, transparent 85%),
        radial-gradient(at 100% 99%, hsl(217, 19%, 88%) 0px, transparent 85%)
      `
      : `linear-gradient(135deg, #1f2940 0%, #161a22 45%, #11141b 100%)`,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.mode === "light"
        ? "inset 0 0 0 1px rgba(107,114,128,0.25), 0 10px 40px -10px rgba(107,114,128,0.25)"
        : "inset 0 0 0 1px rgba(156,163,175,0.3), 0 10px 40px -10px rgba(107,114,128,0.4)",
    },
  },
  cardInner: {
    position: "relative",
    padding: 24,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: { padding: 32 },
    [theme.breakpoints.up("md")]: { padding: 40 },
  },

  logoImg: {
    display: "block",
    margin: "0 auto 16px",
    maxWidth: 150,
    height: "auto",
  },

  header: {
    marginBottom: 24,
  },
  secureRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: theme.mode === "light" ? "#4b5563" : "#9ca3af",
    marginBottom: 10,
  },
  secureDot: {
    height: 6,
    width: 6,
    borderRadius: "50%",
    background: theme.mode === "light"
      ? "rgba(16, 185, 129, 0.9)"
      : "rgba(52, 211, 153, 0.85)",
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    letterSpacing: "-0.01em",
    color: theme.mode === "light" ? "#111827" : "#ffffff",
    margin: 0,
    [theme.breakpoints.up("sm")]: { fontSize: 26 },
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },

  fieldLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 500,
    color: theme.mode === "light" ? "#374151" : "#d1d5db",
    marginBottom: 6,
  },

  inputWrap: {
    position: "relative",
  },
  inputIconLeft: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 12,
    display: "flex",
    alignItems: "center",
    color: theme.mode === "light" ? "#6b7280" : "#9ca3af",
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    borderRadius: 10,
    background: theme.mode === "light"
      ? "rgba(255, 255, 255, 0.85)"
      : "rgba(17, 24, 39, 0.5)",
    color: theme.mode === "light" ? "#111827" : "#f3f4f6",
    boxShadow: theme.mode === "light"
      ? "inset 0 0 0 1px rgba(156, 163, 175, 0.5)"
      : "inset 0 0 0 1px rgba(75, 85, 99, 0.3)",
    border: "none",
    outline: "none",
    padding: "10px 40px",
    fontSize: 13,
    fontFamily: "inherit",
    transition: "box-shadow 0.15s ease",
    "&::placeholder": {
      color: theme.mode === "light" ? "#9ca3af" : "#6b7280",
    },
    "&:focus": {
      boxShadow: theme.mode === "light"
        ? "inset 0 0 0 2px rgba(75, 85, 99, 0.55)"
        : "inset 0 0 0 2px rgba(156, 163, 175, 0.5)",
    },
  },

  submitBtn: {
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 500,
    color: "#ffffff",
    background: "linear-gradient(to right, #374151, #4b5563)",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    boxShadow:
      "inset 0 -2px 25px -4px rgba(255,255,255,0.2), inset 0 0 0 1px rgba(255,255,255,0.10)",
    transition: "all 0.3s ease",
    fontFamily: "inherit",
    "&:hover": {
      background: "linear-gradient(to right, #4b5563, #6b7280)",
      boxShadow:
        "inset 0 -2px 25px -4px rgba(255,255,255,0.2), inset 0 0 0 1px rgba(209,213,219,0.4), 0 8px 24px -8px rgba(0,0,0,0.5)",
    },
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
    "&:focus": { outline: "none" },
  },

  footerRow: {
    marginTop: 16,
    display: "flex",
    justifyContent: "center",
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: theme.mode === "light" ? "#1f2937" : "#d1d5db",
    textDecoration: "none",
    fontWeight: 500,
    "&:hover": {
      color: theme.mode === "light" ? "#111827" : "#ffffff",
      textDecoration: "underline",
      textUnderlineOffset: 4,
    },
  },
}));

const ForgotPassword = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);

  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);

  // Força re-render quando o idioma muda
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const onLangChanged = () => forceUpdate((n) => n + 1);
    i18n.on("languageChanged", onLangChanged);
    return () => i18n.off("languageChanged", onLangChanged);
  }, []);

  const languageOptions = [
    { code: "pt-BR", shortCode: "pt", flag: "/flags/br.png", name: "Português" },
    { code: "en", shortCode: "en", flag: "/flags/us.png", name: "English" },
    { code: "es", shortCode: "es", flag: "/flags/es.png", name: "Español" },
    { code: "tr", shortCode: "tr", flag: "/flags/tr.png", name: "Türkçe" },
    { code: "ar", shortCode: "ar", flag: "/flags/sa.png", name: "العربية" },
  ];

  const currentLanguage = localStorage.getItem("i18nextLng") || "pt-BR";
  const selectedLanguage = languageOptions.find(
    lang => lang.code === currentLanguage || lang.shortCode === currentLanguage
  ) || languageOptions[0];

  const isLangSelected = (lang) =>
    lang.code === currentLanguage || lang.shortCode === currentLanguage;

  const langHeaderLabel = (() => {
    const l = (currentLanguage || "pt").split("-")[0];
    return l === "en" ? "Language" : l === "es" ? "Idioma" : l === "tr" ? "Dil" : l === "ar" ? "اللغة" : "Idioma";
  })();

  const getLogoPath = () => {
    const isDark = theme.mode === "dark";
    if (isDark && typeof theme.calculatedLoginLogoDark === "function") {
      return theme.calculatedLoginLogoDark();
    }
    if (!isDark && typeof theme.calculatedLoginLogoLight === "function") {
      return theme.calculatedLoginLogoLight();
    }
    return isDark
      ? colorMode.appLogoLoginDark || colorMode.appLogoDark || "/logo-dark.png"
      : colorMode.appLogoLoginLight || colorMode.appLogoLight || "/logo-light.png";
  };

  useEffect(() => {
    document.body.classList.add("forgot-password-page");
    return () => {
      document.body.classList.remove("forgot-password-page");
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setEnviando(false);
      setEnviado(true);
      toast.success(i18n.t("forgotPassword.toasts.success"));
      setTimeout(() => setEnviado(false), 2000);
    } catch (err) {
      setEnviando(false);
      toastError(err);
    }
  };

  const handleLanguageOpen = (event) => setLanguageMenuAnchor(event.currentTarget);
  const handleLanguageClose = () => setLanguageMenuAnchor(null);
  const handleLanguageChange = async (languageCode) => {
    try {
      await i18n.changeLanguage(languageCode);
      localStorage.setItem("i18nextLng", languageCode);
      handleLanguageClose();
      window.location.reload();
    } catch (err) {
      console.error("Erro ao alterar idioma:", err);
    }
  };

  return (
    <>
      <Helmet>
        <title>{i18n.t("forgotPassword.title")}</title>
      </Helmet>

      <div className={classes.root}>
        <div className={classes.topRightControls}>
          <div className={classes.langTriggerButton} onClick={handleLanguageOpen}>
            <img
              src={selectedLanguage.flag}
              alt={selectedLanguage.name}
              className={classes.langTriggerFlag}
            />
          </div>
          <ThemeToggle scale={0.75} />
        </div>

        <Popover
          open={Boolean(languageMenuAnchor)}
          anchorEl={languageMenuAnchor}
          onClose={handleLanguageClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          PaperProps={{ className: classes.langPopoverPaper }}
          disableScrollLock
        >
          <div className={classes.langHeader}>
            <Typography className={classes.langHeaderTitle}>
              {langHeaderLabel}
            </Typography>
          </div>
          <div className={classes.langOptionsList}>
            {languageOptions.map((lang) => {
              const selected = isLangSelected(lang);
              return (
                <div
                  key={lang.code}
                  className={`${classes.langOptionItem} ${selected ? classes.langOptionItemSelected : ""}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <img
                    src={lang.flag}
                    alt={lang.name}
                    className={classes.langFlagImg}
                  />
                  <Typography className={classes.langOptionName}>
                    {lang.name}
                  </Typography>
                  <div className={classes.langCheckWrapper}>
                    {selected && <CheckIcon />}
                  </div>
                </div>
              );
            })}
          </div>
        </Popover>

        <div className={classes.cardWrap}>
          <div className={classes.blurLayer}>
            <div className={classes.blurOrb1} />
            <div className={classes.blurOrb2} />
          </div>
          <div className={classes.borderGradient} />

          <div className={classes.card}>
            <div className={classes.cardInner}>
              <img
                src={getLogoPath()}
                alt={i18n.t("login.logoAlt")}
                className={classes.logoImg}
              />

              <div className={classes.header}>
                <div className={classes.secureRow}>
                  <span className={classes.secureDot} />
                  <span>{i18n.t("login.secureArea")}</span>
                </div>
                <h2 className={classes.title}>{i18n.t("forgotPassword.title")}</h2>
              </div>

              <form className={classes.form} onSubmit={handleSubmit} noValidate>
                <div>
                  <label htmlFor="forgot-email" className={classes.fieldLabel}>
                    {i18n.t("forgotPassword.form.emailLabel")}
                  </label>
                  <div className={classes.inputWrap}>
                    <span className={classes.inputIconLeft}>
                      <Mail size={16} strokeWidth={1.5} />
                    </span>
                    <input
                      id="forgot-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      placeholder={i18n.t("login.emailPlaceholder")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={enviando}
                      className={classes.input}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className={classes.submitBtn}
                  disabled={enviando || enviado}
                >
                  {enviando
                    ? i18n.t("forgotPassword.loading.sending")
                    : enviado
                      ? i18n.t("forgotPassword.loading.sent")
                      : i18n.t("forgotPassword.form.submitButton")}
                </button>

                <div className={classes.footerRow}>
                  <RouterLink to="/login" className={classes.backLink}>
                    <ArrowLeft size={14} strokeWidth={1.8} />
                    {i18n.t("forgotPassword.form.backToLogin")}
                  </RouterLink>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
